import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { AdminModerationHistoryResponseDTO } from '@/api/generated/model';
import { ROUTES } from '@/app/routes';
import { cn } from '@/lib/utils';
import {
  historyEntryShowsRiskCause,
  isCoopScanningAction,
  isCoopVerdictAction,
  pendingScoreBarColor,
  pendingScoreChipClass,
  scoreBarColor,
  scoreChipClass,
  type HistoryRiskIndicator,
} from '@/features/moderation/lib/combinedRiskDisplay';

// Shared moderation-history card (port of CMP SharedModerationHistoryCard). Rendered on both the
// user detail (contentType=U) and content detail (contentType=P) pages; fed by
// getModerationHistoryByContent. Each entry shows action, moderator (name + role), timestamp,
// attributed Coop Policies when the ledger named them, and a note.

const DESCRIPTION_COLLAPSE_CHARS = 160;

function formatWhen(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function subjectLabel(entry: AdminModerationHistoryResponseDTO): string | null {
  const type = (entry.contentType ?? '').trim().toUpperCase();
  if (!entry.contentId) return null;
  if (type === 'P') return `Post #${entry.contentId}`;
  if (type === 'U') return `User #${entry.contentId}`;
  return `#${entry.contentId}`;
}

function sourceLabel(source?: string): string | null {
  switch ((source ?? '').trim().toUpperCase()) {
    case 'TIPMI_LEDGER':
      return 'TipMi ledger';
    case 'COOP':
      return 'Coop';
    case 'BEDROCK':
    case 'FRONTIER_MODEL':
    case 'CLOUD':
      // Cloud / Bedrock image-health audits display as Cloud.
      return 'Cloud';
    // Legacy merged pipeline source — keep readable until clients refresh.
    case 'PIPELINE':
      return 'Pipeline';
    case 'ADMIN_STATUS':
      return 'Admin';
    case 'REPORT':
      return 'Report';
    default:
      return null;
  }
}

/** Cloud / Bedrock image-health history labels show as Cloud (not GPU). */
function displayHistoryAction(
  action: string | undefined,
  riskIndicators?: HistoryRiskIndicator[],
): string {
  const a = (action ?? '').trim();
  if (!a) return '—';
  if (
    a === 'Cloud · Image health'
    || a === 'Bedrock · Image health'
    || a === 'Frontier Model · Image health'
  ) {
    return 'Cloud · Image health';
  }
  // Cloud deep sometimes wrote GPU · Image health while scoring with Luna.
  if (a === 'GPU · Image health' || a === 'Gemma · Image health') {
    const hasCloud = riskIndicators?.some(
      (i) => i.id === 'meta.cloud_model' || i.id === 'meta.frontier_model',
    );
    const hasGpu = riskIndicators?.some((i) => i.id === 'meta.gpu_model');
    if (hasCloud && !hasGpu) {
      return 'Cloud · Image health';
    }
  }
  return a;
}

function displayModeratorName(
  moderator: string,
  displayedAction: string,
): string {
  const name = moderator.trim();
  if (!name) return name;
  if (displayedAction === 'Cloud · Image health') {
    if (
      name === 'GPU'
      || name === 'Gemma'
      || name === 'Cloud'
      || name === 'Bedrock'
      || name === 'Frontier Model'
    ) {
      return 'Cloud';
    }
  }
  return name;
}

/** True when `action` already names the source (e.g. "TipMi ledger · Rejected"). */
function actionIncludesSource(action: string | undefined, source: string | null): boolean {
  if (!action || !source) return false;
  return action.toLowerCase().includes(source.toLowerCase());
}

/** Color the action pill to match Coop / TipMi ledger badges on content detail. */
function actionPillClass(action: string | undefined): string {
  const upper = (action ?? '').toUpperCase();
  if (
    upper.includes('REJECT')
    || upper.includes('TAKEDOWN')
    || upper.includes('TAKEN DOWN')
    || upper.includes('TERMINAT')
    || upper.includes('BAN')
  ) {
    return 'bg-red-100 text-red-800 border-red-200 dark:border-rose-400/30 dark:bg-rose-400/10 dark:text-rose-300';
  }
  if (upper.includes('APPROVE') || upper.includes('BYPASS') || upper.includes('LIST')) {
    return 'bg-green-100 text-green-800 border-green-200 dark:border-lime-400/30 dark:bg-lime-400/10 dark:text-lime-300';
  }
  if (upper.includes('ESCALAT') || upper.includes('HUMAN') || upper.includes('WARN')) {
    return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300';
  }
  if (upper.includes('SCAN') || upper.includes('PENDING') || upper.includes('PROCESS') || upper.includes('OPEN')) {
    return 'bg-sky-100 text-sky-800 border-sky-200 dark:border-sky-400/30 dark:bg-sky-400/10 dark:text-sky-300';
  }
  if (upper.includes('IMAGE HEALTH') || upper.includes('BEDROCK')) {
    return 'bg-violet-100 text-violet-800 border-violet-200 dark:border-violet-400/30 dark:bg-violet-400/10 dark:text-violet-300';
  }
  return 'font-normal';
}

function HistoryDescription({ text }: { text: string }) {
  const long = text.length > DESCRIPTION_COLLAPSE_CHARS;
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-md border border-border/60 bg-muted/30 px-2.5 py-2">
      <p
        className={cn(
          'text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap break-words',
          !expanded && long && 'line-clamp-3',
        )}
      >
        {text}
      </p>
      {long && (
        <button
          type="button"
          className="mt-1.5 text-xs font-medium text-primary underline-offset-2 hover:underline"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

/** Model metadata chip (GPU / Cloud / Frontier) — no score meter. */
function ModelMetaChip({ indicator }: { indicator: HistoryRiskIndicator }) {
  const isCloudOrFrontier =
    indicator.id === 'meta.cloud_model'
    || indicator.id.startsWith('meta.cloud_model.')
    || indicator.id === 'meta.frontier_model';
  return (
    <Badge
      variant="outline"
      title={indicator.title}
      className={cn(
        // Match action pill sizing (`px-2.5 py-0.5 text-xs`) so chips align on one row.
        'w-fit max-w-full whitespace-normal rounded-full px-2.5 py-0.5 text-xs font-medium leading-snug',
        isCloudOrFrontier
          ? 'border-violet-300/50 bg-violet-500/10 text-violet-700 dark:border-violet-400/40 dark:bg-violet-400/10 dark:text-violet-300'
          : 'border-sky-300/50 bg-sky-500/10 text-sky-800 dark:border-sky-400/40 dark:bg-sky-400/10 dark:text-sky-300',
      )}
    >
      {indicator.label}
    </Badge>
  );
}

/** Compact risk degree indicator (chip + thin meter) for policy_max or a Policy confidence. */
function RiskDegreeIndicator({
  indicator,
  pending = false,
}: {
  indicator: HistoryRiskIndicator;
  /** While Coop is scanning, do not paint low scores as green “clear”. */
  pending?: boolean;
}) {
  const pct = Math.min(Math.max(indicator.score, 0), 1) * 100;
  const barClass = pending ? pendingScoreBarColor() : scoreBarColor(indicator.score);
  const chipClass = pending ? pendingScoreChipClass() : scoreChipClass(indicator.score);
  return (
    <span
      className="inline-flex items-center gap-1.5"
      title={pending ? `${indicator.title}\nScores still provisional (Coop scanning).` : indicator.title}
    >
      <span
        className="relative h-1.5 w-10 shrink-0 overflow-hidden rounded-full bg-muted"
        aria-hidden
      >
        <span
          className={cn('absolute inset-y-0 left-0 rounded-full', barClass)}
          style={{ width: `${pct}%` }}
        />
      </span>
      <Badge
        variant="outline"
        className={cn(
          'rounded-full px-2 py-0 text-[10px] font-medium tabular-nums',
          chipClass,
        )}
      >
        {indicator.label}
      </Badge>
    </span>
  );
}

function splitRiskIndicators(indicators: HistoryRiskIndicator[]): {
  scores: HistoryRiskIndicator[];
  models: HistoryRiskIndicator[];
} {
  const scores: HistoryRiskIndicator[] = [];
  const models: HistoryRiskIndicator[] = [];
  for (const indicator of indicators) {
    if (indicator.kind === 'meta') models.push(indicator);
    else scores.push(indicator);
  }
  return { scores, models };
}

export function ModerationHistoryList({
  entries,
  isLoading,
  isError,
  onRetry,
  limit,
  viewAllTo,
  footer,
  hideSubject = false,
  riskIndicators,
  riskCause,
}: {
  entries: AdminModerationHistoryResponseDTO[];
  isLoading: boolean;
  isError: boolean;
  onRetry?: () => void;
  /** When set, only the first N entries are shown (list is already newest-first). */
  limit?: number;
  /** Shown when `limit` hides remaining entries. */
  viewAllTo?: string;
  /** Optional footer (e.g. pagination) rendered inside the card under the list. */
  footer?: ReactNode;
  /** Hide Post/User subject links (e.g. already on that content detail page). */
  hideSubject?: boolean;
  /** Policy max + 12 Policy confidence chips from Coop intelligence (content detail only). */
  riskIndicators?: HistoryRiskIndicator[];
  /** Why the post landed in the high-risk band (caption text vs video frame). */
  riskCause?: string | null;
}) {
  const visible = limit != null ? entries.slice(0, limit) : entries;
  const hiddenCount = limit != null ? Math.max(0, entries.length - limit) : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Moderation History</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <div className="space-y-2 py-4 text-center">
            <p className="text-sm text-destructive">Failed to load moderation history.</p>
            {onRetry && (
              <Button size="sm" variant="outline" onClick={onRetry}>
                Retry
              </Button>
            )}
          </div>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No moderation history.</p>
        ) : (
          <>
            <ul className="divide-y">
              {visible.map((e, i) => {
                const subject = hideSubject ? null : subjectLabel(e);
                const displayedAction = displayHistoryAction(e.action, riskIndicators);
                const source = sourceLabel(e.source);
                const showSourceBadge = Boolean(
                  source && !actionIncludesSource(displayedAction, source),
                );
                const moderator = displayModeratorName(e.moderatorName || '', displayedAction);
                const role = (e.moderatorRole || '').trim();
                // Skip moderator name when it only repeats the source (e.g. "TipMi ledger" / "Coop").
                const showModerator =
                  Boolean(moderator) &&
                  moderator.toLowerCase() !== 'system' &&
                  (!source || moderator.toLowerCase() !== source.toLowerCase());
                // Prefer a concrete role; hide the "Automated" chip when a human name is shown.
                const showRole =
                  Boolean(role) &&
                  !(showModerator && role.toLowerCase() === 'automated');
                const postLink =
                  (e.contentType ?? '').trim().toUpperCase() === 'P' && e.contentId
                    ? ROUTES.contentDetails(String(e.contentId))
                    : null;
                const hasMeta = showModerator || showRole || showSourceBadge || subject;
                const showRisk =
                  (riskIndicators?.length ?? 0) > 0
                  && isCoopVerdictAction(e.action, e.source);
                const showCause = Boolean(riskCause) && historyEntryShowsRiskCause(e.action, e.source);
                const scoresPending = isCoopScanningAction(e.action);
                const { scores: scoreIndicators, models: modelIndicators } = showRisk && riskIndicators
                  ? splitRiskIndicators(riskIndicators)
                  : { scores: [], models: [] };

                return (
                  <li
                    key={e.id ?? `${e.action}-${e.createdAt}-${i}`}
                    className="space-y-2 py-3 first:pt-0 last:pb-0"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 flex-col gap-1.5">
                        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                          <Badge
                            variant="outline"
                            className={cn(
                              'w-fit max-w-full whitespace-normal rounded-full px-2.5 py-0.5 text-xs font-medium leading-snug',
                              actionPillClass(displayedAction),
                            )}
                          >
                            {displayedAction}
                          </Badge>
                          {modelIndicators.map((indicator) => (
                            <ModelMetaChip key={indicator.id} indicator={indicator} />
                          ))}
                        </div>
                        {scoreIndicators.length > 0 && (
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            {scoreIndicators.map((indicator) => (
                              <RiskDegreeIndicator
                                key={indicator.id}
                                indicator={indicator}
                                pending={scoresPending}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="shrink-0 pt-0.5 text-xs tabular-nums text-muted-foreground">
                        {formatWhen(e.createdAt)}
                      </span>
                    </div>

                    {hasMeta && (
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                        {showModerator && <span>{moderator}</span>}
                        {showRole && (
                          <Badge variant="outline" className="rounded-full font-normal">
                            {role}
                          </Badge>
                        )}
                        {showSourceBadge && source && (
                          <Badge variant="secondary" className="rounded-full font-normal">
                            {source}
                          </Badge>
                        )}
                        {subject &&
                          (postLink ? (
                            <Link
                              to={postLink}
                              className="text-xs text-primary underline-offset-2 hover:underline"
                            >
                              {subject}
                            </Link>
                          ) : (
                            <span>{subject}</span>
                          ))}
                      </div>
                    )}

                    {showCause && (
                      <p className="text-sm text-foreground">{riskCause}</p>
                    )}

                    {(e.violatedPolicies?.length ?? 0) > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-xs text-muted-foreground">Policies</span>
                        {e.violatedPolicies?.map((name) => (
                          <Badge
                            key={name}
                            variant="outline"
                            className="rounded-full border-red-200 bg-red-50 px-2 py-0 text-[10px] font-medium text-red-900 dark:border-rose-400/30 dark:bg-rose-400/10 dark:text-rose-300"
                          >
                            {name}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {e.description && <HistoryDescription text={e.description} />}
                  </li>
                );
              })}
            </ul>
            {hiddenCount > 0 && viewAllTo && (
              <div className="border-t pt-3">
                <Link
                  to={viewAllTo}
                  className="text-sm font-medium text-primary underline-offset-2 hover:underline"
                >
                  View all {entries.length} moderation history entries
                </Link>
              </div>
            )}
            {footer}
          </>
        )}
      </CardContent>
    </Card>
  );
}
